/**
 * Customer Slide Lookup - uses /customer/slides/lookup (customer from auth).
 * No customer selector; no Job ID lookup.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ApplicationMultiSelectDropdown from "../components/ApplicationMultiSelectDropdown";
import InfoTip from "../components/InfoTip";
import PageTour from "../components/PageTour";
import { InlineTip, ThWithTip } from "../components/FieldTip";
import { SLIDES_LOOKUP_TOUR_STEPS } from "../tour/portalTour";
import { authedGet } from "../api/customerAuthedFetch";
import adminStyles from "../ui/AdminStyles";

const PAGE_TITLE = "Slide Lookup";

const TIPS = {
  timezone:
    "Timezone for the search window and Started/Finished columns. Underlying times are stored in UTC.",
  slideId:
    "Optional. Match slides whose identifier contains this text. Leave blank to search by time range only.",
  applications:
    "Limit results to one or more applications enabled for your organization. Empty means all enabled applications.",
  allTime:
    "When checked, ignore the date/time range and search across all retained history for your organization.",
  range: "Start and end of the search window in the selected timezone (inclusive).",
  lookup: "Run the search with the current filters. Results are limited; use Load more when available.",
  colSlide: "Slide identifier. Click to open slide details for this processing job.",
  colApplication: "Catalog application that processed this slide (for example AIxURO).",
  colStarted: "When processing started, shown in the selected timezone.",
  colFinished: "When processing finished, shown in the selected timezone.",
  colStatus: "Pipeline outcome for this job (for example finished or failed).",
  colMessage: "Last status message from processing, if any.",
};

function todayLocalISODate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatInTimezone(isoUtc, timeZone) {
  if (!isoUtc) return "—";
  return new Intl.DateTimeFormat("sv-SE", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(isoUtc));
}

function SlideStatusBadge({ status }) {
  const ok = (status || "").toLowerCase() === "finished";
  return (
    <span style={{ ...adminStyles.badge, background: ok ? "#16a34a" : "#dc2626" }}>
      {status || "UNKNOWN"}
    </span>
  );
}

export default function CustomerSlideLookup() {
  const navigate = useNavigate();
  const [slideId, setSlideId] = useState("");
  const [startDate, setStartDate] = useState(todayLocalISODate());
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState(todayLocalISODate());
  const [endTime, setEndTime] = useState("23:59");
  const [allTime, setAllTime] = useState(false);
  const [applications, setApplications] = useState([]);
  const [applicationOptions, setApplicationOptions] = useState([
    { id: "AIxURO", label: "AIxURO" },
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const ALL_TIMEZONES = Intl.supportedValuesOf("timeZone");
  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [rows, setRows] = useState([]);
  const [searched, setSearched] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchedKeyRef = useRef("");

  function filtersFromSearchParams(sp) {
    const isAllTime = sp.get("all_time") === "true";
    const appsRaw = sp.get("applications");
    return {
      slideId: sp.get("slide_identifier") || "",
      timezone: sp.get("timezone") || timezone,
      allTime: isAllTime,
      startDate: isAllTime ? startDate : (sp.get("start_date") || todayLocalISODate()),
      startTime: isAllTime ? startTime : (sp.get("start_time") || "00:00"),
      endDate: isAllTime ? endDate : (sp.get("end_date") || todayLocalISODate()),
      endTime: isAllTime ? endTime : (sp.get("end_time") || "23:59"),
      applications: appsRaw
        ? appsRaw.split(",").map((s) => s.trim()).filter(Boolean)
        : [],
    };
  }

  function lookupKey(f) {
    return [
      f.timezone,
      f.allTime ? "all" : `${f.startDate}T${f.startTime}|${f.endDate}T${f.endTime}`,
      f.slideId,
      (f.applications || []).join(","),
    ].join("::");
  }

  useEffect(() => {
    authedGet("/customer/profile")
      .then((data) => {
        const enabled = Array.isArray(data?.enabled_applications)
          ? data.enabled_applications.map(String)
          : ["AIxURO"];
        setApplicationOptions(
          (enabled.length ? enabled : ["AIxURO"]).map((id) => ({
            id,
            label: id,
          }))
        );
      })
      .catch(() => {
        /* keep AIxURO default */
      });
  }, []);

  useEffect(() => {
    const slideIdentifier = searchParams.get("slide_identifier");
    setSlideId(slideIdentifier || "");
    const tz = searchParams.get("timezone");
    if (tz) setTimezone(tz);
    const isAllTime = searchParams.get("all_time") === "true";
    setAllTime(isAllTime);
    if (!isAllTime) {
      setStartDate(searchParams.get("start_date") || todayLocalISODate());
      setStartTime(searchParams.get("start_time") || "00:00");
      setEndDate(searchParams.get("end_date") || todayLocalISODate());
      setEndTime(searchParams.get("end_time") || "23:59");
    }
    const appsRaw = searchParams.get("applications");
    setApplications(
      appsRaw ? appsRaw.split(",").map((s) => s.trim()).filter(Boolean) : []
    );
  }, [searchParams]);

  function syncUrlFromState() {
    const params = {};
    if (slideId) params.slide_identifier = slideId;
    params.timezone = timezone;
    if (applications.length) params.applications = applications.join(",");
    if (allTime) params.all_time = "true";
    else {
      params.start_date = startDate;
      params.start_time = startTime;
      params.end_date = endDate;
      params.end_time = endTime;
    }
    setSearchParams(params);
  }

  async function runLookup(filters, { append = false } = {}) {
    setError(null);
    if (filters.allTime && !filters.slideId) {
      setError("'Search all time' requires a slide identifier");
      return;
    }
    if (!append) {
      setRows([]);
      setSearched(true);
      setNextToken(null);
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.slideId) params.set("slide_identifier", filters.slideId);
      params.set("timezone", filters.timezone);
      if (filters.allTime) params.set("all_time", "true");
      else {
        params.set("start_datetime", `${filters.startDate}T${filters.startTime}:00`);
        params.set("end_datetime", `${filters.endDate}T${filters.endTime}:00`);
      }
      if (filters.applications?.length) {
        params.set("applications", filters.applications.join(","));
      }
      if (append && nextToken) params.set("next_token", nextToken);

      const data = await authedGet(`/customer/slides/lookup?${params.toString()}`);
      setRows((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setNextToken(data.next_token || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function lookupSlides({ append = false } = {}) {
    const filters = {
      slideId,
      timezone,
      allTime,
      startDate,
      startTime,
      endDate,
      endTime,
      applications,
    };
    if (!append) {
      searchedKeyRef.current = lookupKey(filters);
      syncUrlFromState();
    }
    await runLookup(filters, { append });
  }

  useEffect(() => {
    if (searchParams.get("all_time") === "true") return;
    if (!searchParams.get("start_date") || !searchParams.get("end_date")) return;
    const filters = filtersFromSearchParams(searchParams);
    const key = lookupKey(filters);
    if (searchedKeyRef.current === key) return;
    searchedKeyRef.current = key;
    void runLookup(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    document.title = PAGE_TITLE;
  }, []);

  return (
    <div style={{ maxWidth: "100%" }}>
      <div
        data-tour="slides-lookup-intro"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>{PAGE_TITLE}</h2>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <PageTour tourId="slides-lookup" steps={SLIDES_LOOKUP_TOUR_STEPS} />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <select
              data-tour="slides-lookup-timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {ALL_TIMEZONES.map((tz) => (
                <option key={tz} value={tz}>{tz}</option>
              ))}
            </select>
            <InfoTip text={TIPS.timezone} align="end" />
          </span>
        </div>
      </div>

      <div style={{ ...styles.controls, marginTop: 12 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input
            data-tour="slides-lookup-slide-id"
            placeholder="Slide Identifier"
            value={slideId}
            onChange={(e) => setSlideId(e.target.value)}
            style={{ minWidth: 260 }}
          />
          <InfoTip text={TIPS.slideId} />
        </span>
        <div
          data-tour="slides-lookup-applications"
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <ApplicationMultiSelectDropdown
            options={applicationOptions}
            value={applications}
            onChange={setApplications}
          />
          <InfoTip text={TIPS.applications} />
        </div>
        <label style={styles.inlineLabel} data-tour="slides-lookup-all-time">
          <input type="checkbox" checked={allTime} onChange={(e) => setAllTime(e.target.checked)} />
          <InlineTip label="Search all time" tip={TIPS.allTime} />
        </label>
        <span data-tour="slides-lookup-range" style={{ display: "inline-flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          {!allTime && (
            <>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              <span>→</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              <InfoTip text={TIPS.range} />
            </>
          )}
          {allTime ? (
            <span style={{ color: "#6b7280", fontSize: 13 }}>Date range hidden while searching all time</span>
          ) : null}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <button
            data-tour="slides-lookup-submit"
            onClick={() => lookupSlides()}
            disabled={loading}
          >
            Look Up
          </button>
          <InfoTip text={TIPS.lookup} />
        </span>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div data-tour="slides-lookup-results" style={{ marginTop: 20 }}>
        {searched && !loading && rows.length === 0 && <div>No matching slides found.</div>}
        {rows.length > 0 && (
          <table style={adminStyles.table}>
            <thead>
              <tr>
                <th style={adminStyles.th}>#</th>
                <ThWithTip tip={TIPS.colSlide}>Slide ID</ThWithTip>
                <ThWithTip tip={TIPS.colApplication}>Application</ThWithTip>
                <ThWithTip tip={TIPS.colStarted}>Started</ThWithTip>
                <ThWithTip tip={TIPS.colFinished}>Finished</ThWithTip>
                <ThWithTip tip={TIPS.colStatus}>Status</ThWithTip>
                <ThWithTip tip={TIPS.colMessage}>Message</ThWithTip>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr
                  key={`${r.job_id}-${idx}`}
                  onClick={() => r.job_id && navigate(`/slides/${encodeURIComponent(r.job_id)}`)}
                  style={{
                    backgroundColor: idx % 2 ? "white" : "#fafafa",
                    cursor: r.job_id ? "pointer" : "default",
                  }}
                >
                  <td>{idx + 1}</td>
                  <td>
                    {r.job_id ? (
                      <button
                        type="button"
                        style={adminStyles.linkButton}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/slides/${encodeURIComponent(r.job_id)}`);
                        }}
                      >
                        {r.slide_identifier}
                      </button>
                    ) : (
                      r.slide_identifier
                    )}
                  </td>
                  <td>{r.application || "AIxURO"}</td>
                  <td>{formatInTimezone(r.start_datetime, timezone)}</td>
                  <td>{formatInTimezone(r.finished_at, timezone)}</td>
                  <td><SlideStatusBadge status={r.status} /></td>
                  <td>{r.message || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!searched && !loading && rows.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 14 }}>
            Set filters and click Look Up to see matching slides.
          </div>
        )}
        {nextToken && (
          <button
            style={{ marginTop: 12 }}
            onClick={() => lookupSlides({ append: true })}
            disabled={loading}
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  controls: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  inlineLabel: { display: "flex", gap: 6, fontSize: 13 },
  error: { color: "red", marginTop: 16 },
};
